import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  ClipboardCheck, 
  Search, 
  Camera, 
  MapPin, 
  Clock, 
  User,
  Car,
  AlertTriangle,
  Fuel,
  Gauge,
  ChevronLeft,
  ChevronRight,
  X,
  Loader2,
  FileText,
  CheckCircle,
  Image as ImageIcon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const normalizeInspectionPhotos = (inspection) => {
  const toPhotoObject = (photo, index) => {
    if (!photo) return null;
    if (typeof photo === 'string') {
      return { url: photo, type: `photo_${index + 1}` };
    }
    if (typeof photo === 'object') {
      const url = photo.url || photo.data_url || photo.data || photo.photo_data || photo.base64;
      if (!url) return null;
      return { ...photo, url, type: photo.type || `photo_${index + 1}` };
    }
    return null;
  };

  if (Array.isArray(inspection?.photos) && inspection.photos.length > 0) {
    return inspection.photos.map(toPhotoObject).filter(Boolean);
  }

  const images = inspection?.images;
  if (Array.isArray(images) && images.length > 0) {
    return images.map(toPhotoObject).filter(Boolean);
  }

  if (typeof images === 'string' && images.trim()) {
    try {
      const parsed = JSON.parse(images);
      if (Array.isArray(parsed)) {
        return parsed.map(toPhotoObject).filter(Boolean);
      }
    } catch (_) {
      return [];
    }
  }

  return [];
};

const getInspectionDamageCount = (inspection) => {
  if (Array.isArray(inspection?.ai_summary?.damages_detected)) {
    return inspection.ai_summary.damages_detected.length;
  }
  if (Array.isArray(inspection?.ai_analysis?.analysis?.damages)) {
    return inspection.ai_analysis.analysis.damages.length;
  }
  return 0;
};

const getInspectionOdometer = (inspection) =>
  inspection?.ai_summary?.odometer_reading ||
  inspection?.ai_analysis?.analysis?.odometer ||
  null;

const getInspectionFuelLevel = (inspection) =>
  inspection?.ai_summary?.fuel_level ||
  inspection?.ai_analysis?.analysis?.fuel_level ||
  null;

const InspectionsTab = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = (i18n.language || '').startsWith('en') ? 'en-US' : 'es-ES';
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [filterType, setFilterType] = useState('all');

  const fetchInspections = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      let url = `${BACKEND_URL}/api/admin/inspections`;
      if (filterType !== 'all') {
        url += `?inspection_type=${filterType}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInspections(data.inspections || []);
      }
    } catch (error) {
      console.error('Error fetching inspections:', error);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    fetchInspections();
  }, [fetchInspections]);

  const fetchInspectionDetail = async (inspectionId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/inspections/${inspectionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedInspection(data);
        setCurrentPhotoIndex(0);
        setModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching inspection detail:', error);
    }
  };

  const filteredInspections = inspections.filter(inspection => {
    const searchLower = searchTerm.toLowerCase();
    const plate =
      inspection.vehicle_plate ||
      inspection.service_info?.vehicle_info?.plate ||
      '';
    const brand = inspection.service_info?.vehicle_info?.brand || '';
    const model = inspection.service_info?.vehicle_info?.model || '';
    return (
      plate.toLowerCase().includes(searchLower) ||
      inspection.driver_info?.name?.toLowerCase().includes(searchLower) ||
      brand.toLowerCase().includes(searchLower) ||
      model.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (isoString) => {
    if (!isoString) return t('gps_live_na');
    const date = new Date(isoString);
    return date.toLocaleDateString(dateLocale, { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInspectionTypeBadge = (type) => {
    if (type === 'pickup') {
      return <Badge className="bg-blue-100 text-blue-700">{t('pickup')}</Badge>;
    }
    return <Badge className="bg-green-100 text-green-700">{t('delivery')}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">{t('inspections_loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inspections-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 text-purple-600" />
            {t('inspections_title')}
          </h2>
          <p className="text-gray-600 mt-1">
            {t('inspections_subtitle')}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder={t('inspections_search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant={filterType === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('all')}
          >
            {t('admin_all')}
          </Button>
          <Button 
            variant={filterType === 'pickup' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('pickup')}
          >
            {t('pickup')}
          </Button>
          <Button 
            variant={filterType === 'delivery' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('delivery')}
          >
            {t('delivery')}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{inspections.length}</p>
                <p className="text-sm text-gray-500">{t('inspections_stat_total')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Camera className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {inspections.reduce((acc, i) => acc + normalizeInspectionPhotos(i).length, 0)}
                </p>
                <p className="text-sm text-gray-500">{t('inspections_stat_photos')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {inspections.filter(i => getInspectionDamageCount(i) > 0).length}
                </p>
                <p className="text-sm text-gray-500">{t('inspections_stat_with_damage')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {inspections.filter(i => getInspectionDamageCount(i) === 0).length}
                </p>
                <p className="text-sm text-gray-500">{t('inspections_stat_no_damage')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inspections List */}
      {filteredInspections.length > 0 ? (
        <div className="grid gap-4">
          {filteredInspections.map((inspection) => (
            (() => {
              const normalizedPhotos = normalizeInspectionPhotos(inspection);
              const damageCount = getInspectionDamageCount(inspection);
              const odometer = getInspectionOdometer(inspection);
              const fuelLevel = getInspectionFuelLevel(inspection);
              const vehiclePlate =
                inspection.vehicle_plate ||
                inspection.service_info?.vehicle_info?.plate ||
                t('gps_live_na');
              return (
            <Card 
              key={inspection.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => fetchInspectionDetail(inspection.id)}
            >
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Vehicle Info */}
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      {normalizedPhotos[0]?.url ? (
                        <img 
                          src={normalizedPhotos[0].url} 
                          alt="Preview"
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      {normalizedPhotos.length > 1 && (
                        <span className="absolute -bottom-1 -right-1 bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                          +{normalizedPhotos.length - 1}
                        </span>
                      )}
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4 text-gray-500" />
                        <span className="font-bold text-lg">{vehiclePlate}</span>
                        {getInspectionTypeBadge(inspection.inspection_type)}
                      </div>
                      {inspection.service_info?.vehicle_info && (
                        <p className="text-gray-600 text-sm">
                          {inspection.service_info.vehicle_info.brand} {inspection.service_info.vehicle_info.model}
                        </p>
                      )}
                      {inspection.driver_info && (
                        <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                          <User className="w-3 h-3" />
                          {inspection.driver_info.name}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* AI Summary */}
                  <div className="flex flex-wrap gap-2">
                    {damageCount > 0 ? (
                      <Badge variant="destructive" className="flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {t('inspections_damage_count', { count: damageCount })}
                      </Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        {t('inspections_no_damage_short')}
                      </Badge>
                    )}
                    
                    {odometer && (
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Gauge className="w-3 h-3" />
                        {odometer} km
                      </Badge>
                    )}
                    
                    {fuelLevel && (
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Fuel className="w-3 h-3" />
                        {fuelLevel}
                      </Badge>
                    )}
                  </div>

                  {/* Date & Location */}
                  <div className="text-sm text-gray-500 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <Clock className="w-3 h-3" />
                      {formatDate(inspection.created_at)}
                    </div>
                    {inspection.location?.address && (
                      <div className="flex items-center gap-1 justify-end mt-1">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate max-w-[200px]">{inspection.location.address}</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
              );
            })()
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <ClipboardCheck className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">{t('inspections_empty_title')}</h3>
              <p className="text-gray-500 mt-1">
                {searchTerm ? t('inspections_empty_search') : t('inspections_empty_hint')}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inspection Detail Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedInspection && (
            <>
              {(() => {
                const selectedPhotos = normalizeInspectionPhotos(selectedInspection);
                const selectedDamageCount = getInspectionDamageCount(selectedInspection);
                return (
              <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Car className="w-5 h-5" />
                  {t('inspections_modal_title', { plate: selectedInspection.vehicle_plate || selectedInspection.service_info?.vehicle_info?.plate || t('gps_live_na') })}
                  {getInspectionTypeBadge(selectedInspection.inspection_type)}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                {/* Photo Gallery */}
                {selectedPhotos.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      {t('inspections_photo_gallery', { count: selectedPhotos.length })}
                    </h4>
                    
                    {/* Main Photo */}
                    <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                      <img 
                        src={selectedPhotos[currentPhotoIndex]?.url}
                        alt={t('inspections_photo_alt', { n: currentPhotoIndex + 1 })}
                        className="w-full h-80 object-contain"
                      />
                      
                      {/* Navigation Arrows */}
                      {selectedPhotos.length > 1 && (
                        <>
                          <button 
                            onClick={() => setCurrentPhotoIndex(i => i === 0 ? selectedPhotos.length - 1 : i - 1)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </button>
                          <button 
                            onClick={() => setCurrentPhotoIndex(i => i === selectedPhotos.length - 1 ? 0 : i + 1)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        </>
                      )}
                      
                      {/* Photo Type Badge */}
                      <div className="absolute bottom-2 left-2">
                        <Badge className="bg-black/70 text-white">
                          {selectedPhotos[currentPhotoIndex]?.type || t('inspections_photo_type_default')}
                        </Badge>
                      </div>
                      
                      {/* Counter */}
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-sm">
                        {currentPhotoIndex + 1} / {selectedPhotos.length}
                      </div>
                    </div>

                    {/* Thumbnails */}
                    <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                      {selectedPhotos.map((photo, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentPhotoIndex(index)}
                          className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                            index === currentPhotoIndex ? 'border-blue-500' : 'border-transparent'
                          }`}
                        >
                          <img 
                            src={photo.url} 
                            alt={`Thumbnail ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>

                    {/* AI Analysis for current photo */}
                    {selectedPhotos[currentPhotoIndex]?.ai_analysis && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800">
                          <strong>{t('inspections_ai_analysis_prefix')}</strong> {selectedPhotos[currentPhotoIndex].ai_analysis}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* AI Summary */}
                {selectedInspection.ai_summary && (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      {t('inspections_ai_summary_title')}
                    </h4>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Damages */}
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className={`w-4 h-4 ${selectedInspection.ai_summary.damages_detected?.length ? 'text-red-500' : 'text-green-500'}`} />
                            <span className="font-medium">{t('inspections_damages_detected')}</span>
                          </div>
                          {selectedDamageCount > 0 ? (
                            <ul className="list-disc list-inside text-sm text-gray-600">
                              {(selectedInspection.ai_summary?.damages_detected || selectedInspection.ai_analysis?.analysis?.damages || []).map((damage, i) => (
                                <li key={i}>{damage}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-green-600">{t('inspections_no_damages_message')}</p>
                          )}
                        </CardContent>
                      </Card>

                      {/* Vehicle Stats */}
                      <Card>
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="flex items-center gap-2 text-gray-600">
                                <Gauge className="w-4 h-4" />
                                {t('inspections_odometer')}
                              </span>
                              <span className="font-medium">{getInspectionOdometer(selectedInspection) || t('gps_live_na')}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="flex items-center gap-2 text-gray-600">
                                <Fuel className="w-4 h-4" />
                                {t('inspections_fuel')}
                              </span>
                              <span className="font-medium">{getInspectionFuelLevel(selectedInspection) || t('gps_live_na')}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">{t('inspections_overall_condition')}</span>
                              <Badge variant="outline">{selectedInspection.ai_summary.overall_condition || t('gps_live_na')}</Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}

                {/* Signature */}
                {selectedInspection.signature && (
                  <div>
                    <h4 className="font-semibold mb-3">{t('inspections_signature_title')}</h4>
                    <div className="flex items-center gap-4">
                      {selectedInspection.signature.data_url && (
                        <img 
                          src={selectedInspection.signature.data_url} 
                          alt={t('inspections_signature_alt')}
                          className="h-20 border rounded"
                        />
                      )}
                      {selectedInspection.signature.signer_name && (
                        <span className="text-gray-600">{selectedInspection.signature.signer_name}</span>
                      )}
                    </div>
                  </div>
                )}
                {!selectedInspection.signature && selectedInspection.signature_image && (
                  <div>
                    <h4 className="font-semibold mb-3">{t('inspections_signature_title')}</h4>
                    <div className="flex items-center gap-4">
                      <img
                        src={selectedInspection.signature_image}
                        alt={t('inspections_signature_alt')}
                        className="h-20 border rounded"
                      />
                      {selectedInspection.signed_by && (
                        <span className="text-gray-600">{selectedInspection.signed_by}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Location & Time */}
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-4 h-4" />
                    <span>{selectedInspection.location?.address || t('inspections_location_unavailable')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>{formatDate(selectedInspection.created_at)}</span>
                  </div>
                </div>

                {/* Driver Info */}
                {selectedInspection.driver_info && (
                  <div className="pt-4 border-t">
                    <div className="flex items-center gap-2 text-gray-600">
                      <User className="w-4 h-4" />
                      <span>{t('inspections_performed_by')} <strong>{selectedInspection.driver_info.name}</strong></span>
                      {selectedInspection.driver_info.phone && (
                        <span className="text-blue-600">({selectedInspection.driver_info.phone})</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              </>
                );
              })()}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InspectionsTab;
